<template>
    <common-modal
        height="large"
        width="extra-large-wide"
        @emitEvent="emitEvent($event, ['close'])"
        @close="emitEvent({ name: 'close' })"
    >
        <div class="referral-tracking__referrals-made-card">
            <common-loader v-if="isLoading" :isProcessing="isLoading"></common-loader>
            <p v-else-if="loadError" class="content__error">{{ $t('common.errors.load_failed') }}</p>
            <table class="referral-tracking__referrals-made-table">
                <thead>
                    <tr>
                        <th>{{ $t('pages.referrals.index.referrals_made.id_made') }}</th>
                        <th v-if="!isMobileLarge">{{ $t('pages.referrals.index.referrals_made.stage') }}</th>
                        <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                        <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.timing') }}</th>
                        <th>{{ $t('pages.referrals.index.referrals_made.action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="provider in providers" :key="provider.id">
                        <td class="referral-tracking__card-referral-layout">
                            <div class="user-cell">
                                <div class="user-avatar">
                                    <common-user-avatar size="" :user="getAvatarUser(provider)"></common-user-avatar>
                                </div>
                                <div>
                                    <p class="name">{{ getProviderName(provider) }}</p>
                                    <p class="sub">{{ getProviderSpeciality(provider) }}</p>
                                </div>
                            </div>
                        </td>
                        <td v-if="!isMobileLarge">
                            <div class="referral-tracking__stage">
                                <span :class="getStageClasses(provider)" @click="handleStageAction(provider)">
                                    <span
                                        v-if="isStageLoadingForReferral(provider)"
                                        class="referral-tracking__stage-inline-loader"
                                    ></span>
                                    {{ formatStage(provider) }}
                                </span>

                                <div class="referral-tracking__stage-tooltip-wrapper">
                                    <common-icon
                                        name="info"
                                        size="semi-small"
                                        class="referral-tracking__stage-info-icon"
                                    ></common-icon>
                                    <div class="referral-tracking__stage-tooltip">
                                        {{ getStageTooltip(provider) }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td v-if="!isTablet">
                            <div class="referral-tracking__status-cell">
                                <span :class="getStatusBadgeClass(provider)">
                                    <span class="referral-tracking__status-content">
                                        <span>{{ getStatusText(provider) }}</span>
                                    </span>
                                </span>
                            </div>
                        </td>
                        <td v-if="!isTablet">
                            <span :class="getTimingClass(provider?.stageInfo)">
                                {{ getTimingText(provider) }}
                            </span>
                        </td>
                        <td class="referral-tracking__view-port">
                            <div class="referral-tracking__confirt-detail">
                                <common-button
                                    v-if="hasPendingAdditionalRecordsRequest(provider)"
                                    :label="$t('modals.patient_record.upload')"
                                    type="primary"
                                    size="medium-small"
                                    @click="uploadRecords(provider)"
                                ></common-button>
                                <button
                                    v-else
                                    class="referral-tracking__view-details-btn"
                                    @click="openDetailsModal('referrals_pool')"
                                >
                                    {{ $t('pages.referrals.index.referrals_made.detail') }}
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <referral-modal
            v-if="showViewDetailsModal"
            :showAsInnerModal="true"
            :referral="activeReferral"
            type="made"
            sourceType="referrals_pool"
            :isOpen="showViewDetailsModal"
            :isFetching="isFetching"
            @open-direct-referral="openDirectReferralFromMatchReferal"
            @close="closeDetailsModal"
        ></referral-modal>
        <upload-referral-additional-records
            v-if="showUploadRecordsModal"
            :isOpen="showUploadRecordsModal"
            :provider="selectedProvider"
            :referralPoolId="referralPoolId"
            @close="closeUploadRecordsModal"
            @records-uploaded="handleRecordsUploaded"
        ></upload-referral-additional-records>
    </common-modal>
</template>

<script>
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import StagesEnum from '@/enums/StagesEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import UrgencyEnum from '@/enums/UrgencyEnum'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralModal from './ReferralModal.vue'
    import NextActionsEnum from '@/enums/NextActionsEnum'
    import UploadReferralAdditionalRecords from './UploadReferralAdditionalRecords.vue'

    export default {
        mixins: [ScreenSizeDetectionMixin],
        components: {
            ReferralModal,
            UploadReferralAdditionalRecords,
        },

        props: {
            referralPoolId: { type: Number, required: true },
        },
        data() {
            return {
                providers: [],
                isLoading: true,
                loadError: null,
                PracticeTypeEnum,
                showViewDetailsModal: false,
                activeReferral: {},
                isFetching: false,
                isStageLoading: false,
                selectedProviderForActions: {},
                showUploadRecordsModal: false,
                selectedProvider: null,
            }
        },

        computed: {
            usePhysicianLayout() {
                if (!this.providers.length) return false
                const first = this.providers[0]
                if (first.type === 'external') return true
                return first.practice?.practiceType === PracticeTypeEnum.PHYSICIAN
            },
        },

        methods: {
            itemKey(item, index) {
                if (item.type === 'external') return `ext-${item.providerNpi || item.id || index}`
                return item.id != null ? item.id : `in-pool-${index}`
            },
            getReferralPoolProviders() {
                this.isLoading = true
                this.loadError = null
                ReferralsPoolAPI.getReferralsPoolProviders(this.referralPoolId, {
                    feasibility: true,
                })
                    .then((response) => {
                        this.providers = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : []
                    })
                    .catch(() => {
                        this.loadError = true
                        this.providers = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            getAvatarUser(provider) {
                if (provider.type === 'external') {
                    return {
                        firstName: provider.data.contactInfo.firstName,
                        lastName: provider.data.contactInfo.lastName,
                        avatar: null,
                    }
                }

                return provider
            },
            getProviderName(provider) {
                if (provider.type === 'external') {
                    const contact = provider.data?.contactInfo || {}
                    return [contact.designation, contact.firstName, contact.lastName].filter(Boolean).join(' ')
                }

                return [provider.designation, provider.firstName, provider.lastName].filter(Boolean).join(' ') || provider.name
            },
            getProviderSpeciality(provider) {
                if (provider.type === 'external') {
                    return provider?.speciality || ''
                }
                return provider.specialities[0]?.speciality?.name || ''
            },

            getStageClass(referral) {
                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    return 'referral-tracking__referrals diagnosis'
                }
                const stage = referral?.stageInfo?.stage
                if (!stage) return 'referral-tracking__referrals gray'

                switch (stage) {
                    case StagesEnum.CONFIRMED:
                    case StagesEnum.AWAITING_PATIENT_VISIT:
                        return 'referral-tracking__referrals green'

                    case StagesEnum.REFERRAL_SUBMITTED:
                    case StagesEnum.AWAITING_PROVIDER_PROPOSALS:
                    case StagesEnum.AWAITING_REFERRER_SELECTION:
                    case StagesEnum.PENDING_PROVIDER_CONFIRMATION:
                        return 'referral-tracking__referrals yellow'

                    case StagesEnum.PROPOSALS_CLOSED:
                        return 'referral-tracking__referrals purple'

                    case StagesEnum.SELECT_A_TIME_SLOT:
                    case StagesEnum.PROPOSALS_RECEIVED:
                        return 'referral-tracking__referrals blue'

                    case StagesEnum.CANCELLED_NO_PROPOSALS:
                    case StagesEnum.CANCELLED_NO_SELECTION:
                        return 'referral-tracking__referrals today'

                    case StagesEnum.PROPOSE_TIME_SLOTS:
                        return 'referral-tracking__referrals purple'

                    default:
                        return 'referral-tracking__referrals gray'
                }
            },
            formatStage(referral) {
                let stage = referral?.stageInfo?.stage

                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    stage = StagesEnum.UPLOAD_RECORDS
                }
                if (!stage) return 'N/A'

                return stage
                    .toLowerCase()
                    .split('_')
                    .map((word) => (word.length > 2 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
                    .join(' ')
            },
            getStageTooltip(provider) {
                if (this.hasPendingAdditionalRecordsRequest(provider)) {
                    return NextActionsEnum.UPLOAD_RECORDS
                }
                return provider?.stageInfo?.nextAction
            },
            isStageClickable(provider) {
                const stage = provider?.stageInfo?.stage
                if (stage === StagesEnum.SELECT_A_TIME_SLOT || stage === StagesEnum.PROPOSALS_RECEIVED) {
                    return true
                }
                return false
            },
            isStageLoadingForReferral(provider) {
                return this.isStageLoading && this.selectedProviderForActions?.id === provider.id
            },
            handleStageClick(provider) {
                this.selectedProviderForActions = provider
                const stage = provider?.stageInfo?.stage
                switch (stage) {
                    case StagesEnum.SELECT_A_TIME_SLOT:
                    case StagesEnum.PROPOSALS_RECEIVED:
                        this.chooseSlotMethod({
                            ...provider,
                            referralId: this.referralPoolId,
                        })
                }
            },
            chooseSlotMethod(data) {
                this.$router.push({
                    name: `referrals.made.pool.show`,
                    params: {
                        id: data.referralId,
                    },
                })
            },
            getStageClasses(provider) {
                return [
                    this.getStageClass(provider),
                    {
                        'referral-tracking__referrals-clickable': this.isStageClickable(provider),
                        'referral-tracking__referrals-clickable--loading': this.isStageLoadingForReferral(provider),
                    },
                ]
            },

            handleStageAction(provider) {
                if (this.isStageClickable(provider)) {
                    this.handleStageClick(provider)
                }
            },

            hasPendingAdditionalRecordsRequest(provider) {
                return provider.additionalRecordsRequest?.some((request) => request.status === 'PENDING')
            },
            hasCompletedAdditionalRecordsRequest(provider) {
                return (
                    provider.additionalRecordsRequest?.length > 0 &&
                    provider.additionalRecordsRequest.every((request) => request.status === 'FULFILLED')
                )
            },
            getStatusText(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, ReferralTrackingTypeEnum.MATCH)

                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },

                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }

                return statusMapping[sourceType]?.[status] || status
            },
            getStatusColor(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, ReferralTrackingTypeEnum.MATCH)

                const yellowStatuses = ['CREATED', 'AWAITING_CONFIRMATION']

                const blueStatuses = ['SCHEDULED', 'COMPLETED']

                const redStatuses = ['DECLINED', 'CANCELLED', 'NO_SHOW', 'EXPIRED']

                const greenStatuses = ['CONSULTATION_NOTES_PENDING']

                if (yellowStatuses.includes(displayStatus)) return 'yellow'
                if (blueStatuses.includes(displayStatus)) return 'blue'
                if (redStatuses.includes(displayStatus)) return 'today'
                if (greenStatuses.includes(displayStatus)) return 'green'

                return 'gray'
            },
            getStatusBadgeClass(referral) {
                if (!referral) {
                    return 'referral-tracking__referrals gray'
                }

                return {
                    'referral-tracking__referrals': true,
                    'referral-tracking__status-badge': true,
                    [this.getStatusColor(referral)]: true,
                }
            },
            getTimingText(referral) {
                const stageInfo = referral?.stageInfo
                referral.sourceType = 'referrals_pool'
                if (!stageInfo) return 'N/A'
                if (referral?.sourceType === ReferralTrackingTypeEnum.MATCH) {
                    if (
                        referral.status === ReferralStatusEnum.SCHEDULED ||
                        referral.status === ReferralStatusEnum.DECLINED ||
                        referral.status === ReferralStatusEnum.INACTIVE
                    )
                        return 'N/A'
                    else {
                        const totalHours = stageInfo.hoursLeft

                        if (totalHours <= 0) {
                            return 'N/A'
                        }

                        if (totalHours < 1) {
                            const minutes = Math.ceil(totalHours * 60)
                            return `${minutes} minute${minutes !== 1 ? 's' : ''} left`
                        }

                        const wholeHours = Math.floor(totalHours)
                        const days = Math.floor(wholeHours / 24)
                        const hours = wholeHours % 24

                        if (days > 0 && hours > 0) {
                            return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} left`
                        }

                        if (days > 0) {
                            return `${days} day${days > 1 ? 's' : ''} left`
                        }

                        return `${hours} hour${hours > 1 ? 's' : ''} left`
                    }
                } else if (referral?.sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    return 'N/A'
                }
            },

            getTimingClass(stageInfo) {
                if (!stageInfo) return 'referral-tracking__referrals gray'

                const { urgency, hoursLeft } = stageInfo

                const isThreeHoursLeft = hoursLeft >= 0 && hoursLeft <= 3

                return {
                    'referral-tracking__referrals': true,

                    gray: hoursLeft <= 0,

                    green: urgency === UrgencyEnum.LOW && hoursLeft > 3,

                    yellow: urgency === UrgencyEnum.MEDIUM && hoursLeft > 3,

                    today: isThreeHoursLeft || ([UrgencyEnum.HIGH, UrgencyEnum.CRITICAL].includes(urgency) && hoursLeft > 3),
                }
            },
            openDetailsModal(sourceType) {
                this.isFetching = true
                this.showViewDetailsModal = true
                if (sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    ReferralsAPI.get(this.activeReferral?.referralId, this.$auth.getActiveMember()?.id || this.$auth.getUser().id)
                        .then((res) => {
                            this.activeReferral = res
                        })
                        .finally(() => (this.isFetching = false))
                } else {
                    if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                        ReferralsPoolAPI.getReferralPoolById(this.referralPoolId, 'made', this.$auth.getActiveMember()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    } else {
                        ReferralsPoolAPI.getReferralPoolById(this.referralPoolId, 'made', null, this.$auth.getPractice()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    }
                }
            },
            closeDetailsModal() {
                this.showViewDetailsModal = false
                this.activeReferral = {}
            },
            openDirectReferralFromMatchReferal() {
                this.showViewDetailsModal = false

                this.$nextTick(() => {
                    this.openDetailsModal('referral')
                })
            },
            refreshMatchReferral(referralId) {
                const index = this.referrals.findIndex((r) => r.id === referralId)

                if (index === -1) return

                const existingSourceType = this.referrals[index]?.sourceType

                const request =
                    this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN
                        ? ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              this.$auth.getActiveMember()?.id,
                          )
                        : ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              null,
                              this.$auth.getPractice()?.id,
                          )

                request.then((res) => {
                    res.sourceType = existingSourceType
                    this.$set(this.referrals, index, res)
                })
            },
            uploadRecords(provider) {
                this.selectedProvider = provider
                this.showUploadRecordsModal = true
            },
            closeUploadRecordsModal() {
                this.showUploadRecordsModal = false
                this.selectedProvider = null
            },
            handleRecordsUploaded() {
                this.closeUploadRecordsModal()
                this.emitEvent({ name: 'records-uploaded', payload: this.referralPoolId })
            },
        },

        mounted() {
            this.getReferralPoolProviders()
        },
    }
</script>

<style scoped lang="scss">
    .content {
        max-height: 500px;
        overflow-y: auto;

        &__error {
            padding: 20px;
            text-align: center;
            color: var(--color-error, #c00);
        }
    }
</style>
